import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Stack,
    Chip,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Switch,
    IconButton,
    Tooltip,
    Alert,
} from '@mui/material';
import {
    CheckCircleOutline,
    ErrorOutline,
    SaveOutlined,
    TuneOutlined,
    AddCircleOutline,
    DeleteOutline,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../theme';
import {
    useUpdateOrgDeliveryFormatMutation,
    useUpdateOrgS3DeliveryOverridesMutation,
} from '../../../features/organizations/organizationsApiSlice';

const normalizeFormats = (formats) => {
    return Array.isArray(formats)
        ? [...new Set(
            formats
                .map((x) => String(x || '').trim().toLowerCase())
                .filter((x) => ['csv', 'parquet', 'snowflake'].includes(x))
        )].sort()
        : [];
};

const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

const normalizeOverrides = (items) => {
    return Array.isArray(items)
        ? items.map((item) => ({
            _id: item?._id || '',
            enabled: item?.enabled !== false,
            label: item?.label || '',
            product: item?.product || '',
            release: item?.release || '',
            file_format: item?.file_format || 'parquet',
            destination_path_template: item?.destination_path_template || '',
        }))
        : [];
};

const emptyOverride = () => ({
    enabled: true,
    label: '',
    product: '',
    release: '',
    file_format: 'parquet',
    destination_path_template: 'outgoing/{product_folder}/{yyyy_mm_dd}',
});

const S3DeliveryOverridesAdvanced = ({
    open,
    onClose,
    organization,
    organizationId,
    colors,
}) => {
    const [
        updateOrgS3DeliveryOverrides,
        { isLoading: savingOverrides },
    ] = useUpdateOrgS3DeliveryOverridesMutation();

    const products = Array.isArray(organization?.products)
        ? organization.products
        : [];

    const originalOverrides = useMemo(
        () => normalizeOverrides(organization?.s3_delivery_overrides),
        [organization?.s3_delivery_overrides]
    );

    const [rows, setRows] = useState(originalOverrides);
    const [error, setError] = useState('');


    useEffect(() => {
        if (open) {
            setRows(originalOverrides);
            setError('');
        }
    }, [open, originalOverrides]);

    const updateRow = (index, field, value) => {
        setRows((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                [field]: value,
            };
            return next;
        });
    };

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                ...emptyOverride(),
                product: products[0] || '',
            },
        ]);
    };

    const deleteRow = (index) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
    };

    const buildPayload = () => {
        return rows.map((row) => {
            const clean = {
                enabled: row.enabled !== false,
                product: String(row.product || '').trim(),
                file_format: String(row.file_format || '').trim().toLowerCase(),
                destination_path_template: String(row.destination_path_template || '')
                    .trim()
                    .replace(/^\/+/, '')
                    .replace(/\/+$/, ''),
            };

            if (row._id) clean._id = row._id;
            if (String(row.label || '').trim()) clean.label = String(row.label).trim();
            if (String(row.release || '').trim()) clean.release = String(row.release).trim();

            return clean;
        });
    };

    const validate = (payload) => {
        const errors = [];

        payload.forEach((row, index) => {
            if (!row.product) errors.push(`Row ${index + 1}: product is required`);
            if (!row.file_format) errors.push(`Row ${index + 1}: file format is required`);
            if (!row.destination_path_template) errors.push(`Row ${index + 1}: destination path template is required`);
            if (row.destination_path_template.toLowerCase().startsWith('s3://')) {
                errors.push(`Row ${index + 1}: template must be relative, not a full s3:// URI`);
            }
            if (row.destination_path_template.includes('..')) {
                errors.push(`Row ${index + 1}: template cannot include '..'`);
            }
        });

        return errors;
    };

    const handleSave = async () => {
        const payload = buildPayload();
        const validationErrors = validate(payload);

        if (validationErrors.length) {
            setError(validationErrors.join(' | '));
            return;
        }

        setError('');

        await updateOrgS3DeliveryOverrides({
            id: organizationId,
            s3_delivery_overrides: payload,
        }).unwrap();

        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Advanced S3 Delivery Overrides
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Alert severity="info">
                        These overrides change the generated S3 destination path for specific products.
                        The template is relative to the org bucket/root, not a full s3:// URI.
                    </Alert>

                    {error ? (
                        <Alert severity="error">
                            {error}
                        </Alert>
                    ) : null}

                    {!rows.length ? (
                        <Box
                            sx={{
                                border: `1px dashed ${colors.primary[300]}`,
                                borderRadius: '14px',
                                p: 2,
                            }}
                        >
                            <Typography variant="body1">
                                No custom S3 delivery overrides are configured for this organization.
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {rows.map((row, index) => (
                                <Paper
                                    key={row._id || index}
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        borderRadius: '14px',
                                        background: colors.primary[500],
                                    }}
                                >
                                    <Stack spacing={2}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6">
                                                Override {index + 1}
                                            </Typography>

                                            <Box display="flex" alignItems="center" gap={1}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={row.enabled !== false}
                                                            onChange={(e) => updateRow(index, 'enabled', e.target.checked)}
                                                        />
                                                    }
                                                    label={row.enabled !== false ? 'Enabled' : 'Disabled'}
                                                />

                                                <Tooltip title="Remove override">
                                                    <IconButton
                                                        onClick={() => deleteRow(index)}
                                                        sx={{ color: colors.redAccent[400] }}
                                                    >
                                                        <DeleteOutline />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        <Box
                                            display="grid"
                                            gridTemplateColumns={{ xs: '1fr', md: '1fr 160px 160px' }}
                                            gap={2}
                                        >
                                            <TextField
                                                select={products.length > 0}
                                                label="Product"
                                                value={row.product}
                                                onChange={(e) => updateRow(index, 'product', e.target.value)}
                                                fullWidth
                                            >
                                                {products.map((product) => (
                                                    <MenuItem key={product} value={product}>
                                                        {product}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <TextField
                                                select
                                                label="Release Scope"
                                                value={row.release || ''}
                                                onChange={(e) => updateRow(index, 'release', e.target.value)}
                                                fullWidth
                                            >
                                                <MenuItem value="">Any</MenuItem>
                                                <MenuItem value="Contact">Contact</MenuItem>
                                                <MenuItem value="Digital">Digital</MenuItem>
                                            </TextField>

                                            <TextField
                                                select
                                                label="File Format"
                                                value={row.file_format || 'parquet'}
                                                onChange={(e) => updateRow(index, 'file_format', e.target.value)}
                                                fullWidth
                                            >
                                                <MenuItem value="csv">CSV</MenuItem>
                                                <MenuItem value="parquet">Parquet</MenuItem>
                                            </TextField>
                                        </Box>

                                        <TextField
                                            label="Destination Path Template"
                                            value={row.destination_path_template}
                                            onChange={(e) => updateRow(index, 'destination_path_template', e.target.value)}
                                            fullWidth
                                            helperText="Example: outgoing/maid_ip/{yyyy_mm_dd} or outgoing/ip_maid_da/{yyyy}/{mm}"
                                        />

                                        <TextField
                                            label="Label"
                                            value={row.label || ''}
                                            onChange={(e) => updateRow(index, 'label', e.target.value)}
                                            fullWidth
                                            helperText="Optional internal description"
                                        />

                                        <Box>
                                            <Typography variant="caption" color="textSecondary">
                                                Supported tokens: {'{product_folder}'}, {'{version}'}, {'{version_folder}'}, {'{format}'}, {'{yyyy}'}, {'{mm}'}, {'{dd}'}, {'{yyyy_mm_dd}'}, {'{yyyy-mm-dd}'}, {'{yyyy/mm}'}, {'{yyyy/mm/dd}'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    )}

                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<AddCircleOutline />}
                            onClick={addRow}
                            sx={{
                                borderColor: colors.blueAccent[300],
                                color: colors.greenAccent[200],
                            }}
                        >
                            Add S3 Override
                        </Button>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={savingOverrides}
                    startIcon={<SaveOutlined />}
                    sx={{
                        bgcolor: colors.greenAccent[600],
                        color: colors.grey[100],
                        '&:hover': {
                            bgcolor: colors.greenAccent[700],
                        },
                    }}
                >
                    {savingOverrides ? 'Saving...' : 'Save Overrides'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DeliveryInfo = ({
    organization,
    organizationId,
    s3Config,
    deliveryFormat = [],
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [updateOrgDeliveryFormat, { isLoading: saving }] = useUpdateOrgDeliveryFormatMutation();

    const onpremConfig = s3Config?.infra_data?.onprem_config;
    const snowflakeConfig = s3Config?.infra_data?.snowflake_config;

    const bucketCreated = onpremConfig?.bucket_created;
    const onpremBucket = onpremConfig?.onprem_bucket;
    const s3Uri = onpremConfig?.s3_uri;

    const snowflakeReady = Boolean(
        snowflakeConfig?.database &&
        snowflakeConfig?.schema
    );

    const s3Ready = Boolean(onpremConfig && (bucketCreated || onpremBucket || s3Uri));

    const availableOptions = useMemo(() => {
        const opts = [];
        if (s3Ready) {
            opts.push('csv');
            opts.push('parquet');
        }
        if (snowflakeReady) {
            opts.push('snowflake');
        }
        return opts;
    }, [s3Ready, snowflakeReady]);

    const normalizedDeliveryFormat = useMemo(
        () => normalizeFormats(deliveryFormat),
        [deliveryFormat]
    );

    const [selectedFormats, setSelectedFormats] = useState(normalizedDeliveryFormat);
    const [advancedOpen, setAdvancedOpen] = useState(false);

    useEffect(() => {
        setSelectedFormats((prev) => {
            return arraysEqual(prev, normalizedDeliveryFormat) ? prev : normalizedDeliveryFormat;
        });
    }, [normalizedDeliveryFormat]);

    const toggleFormat = (format) => {
        setSelectedFormats((prev) => {
            const next = prev.includes(format)
                ? prev.filter((x) => x !== format)
                : [...prev, format];

            return next.sort();
        });
    };

    const hasChanges = useMemo(() => {
        return !arraysEqual(selectedFormats, normalizedDeliveryFormat);
    }, [selectedFormats, normalizedDeliveryFormat]);

    const handleSave = async () => {
        await updateOrgDeliveryFormat({
            id: organizationId,
            delivery_format: selectedFormats,
        }).unwrap();
    };

    return (
        <Paper
            elevation={3}
            sx={{
                background: `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[900]})`,
                borderRadius: '20px',
                padding: '20px',
                marginTop: '20px',
            }}
        >
            <Typography variant="h4" mb={3} color="textPrimary">
                Delivery Config
            </Typography>

            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" mb={2} color="textPrimary">
                        S3 Delivery
                    </Typography>

                    {onpremConfig ? (
                        s3Ready ? (
                            <Box>
                                <Typography variant="h6" color="textPrimary">
                                    S3 Bucket Location
                                </Typography>

                                <Typography variant="body1" color="textPrimary" mt={1}>
                                    {onpremBucket
                                        ? `s3://${onpremBucket}/`
                                        : s3Uri
                                            ? `s3://${s3Uri.replace(/^s3:\/\//i, '')}/`
                                            : 'Configured'}
                                </Typography>

                                <Typography variant="body2" color="textSecondary" mt={1}>
                                    Use the /incoming and /outgoing folders for S3 users to access.
                                </Typography>

                                <Box mt={1.5}>
                                    <Chip
                                        icon={<CheckCircleOutline />}
                                        label="S3 Ready"
                                        sx={{
                                            bgcolor: colors.greenAccent[700],
                                            color: colors.grey[100],
                                        }}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="flex-start">
                                <CircularProgress color="inherit" size={24} />
                                <Typography variant="h6" color="textPrimary" ml={2}>
                                    S3 Configuration Pending
                                </Typography>
                            </Box>
                        )
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="flex-start">
                            <ErrorOutline sx={{ fontSize: 32, color: colors.redAccent[500] }} />
                            <Typography variant="h6" color="textPrimary" ml={2}>
                                S3 Delivery Not Configured
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ borderColor: colors.primary[300] }} />

                <Box>
                    <Typography variant="h5" mb={2} color="textPrimary">
                        Snowflake Delivery
                    </Typography>

                    {snowflakeConfig ? (
                        snowflakeReady ? (
                            <Box>
                                <Typography variant="body1" color="textPrimary">
                                    <strong>Database:</strong> {snowflakeConfig.database}
                                </Typography>
                                <Typography variant="body1" color="textPrimary" mt={0.5}>
                                    <strong>Schema:</strong> {snowflakeConfig.schema}
                                </Typography>
                                {snowflakeConfig.table ? (
                                    <Typography variant="body1" color="textPrimary" mt={0.5}>
                                        <strong>Table:</strong> {snowflakeConfig.table}
                                    </Typography>
                                ) : null}
                                {snowflakeConfig.locator ? (
                                    <Typography variant="body2" color="textSecondary" mt={0.5}>
                                        Locator: {snowflakeConfig.locator}
                                    </Typography>
                                ) : null}

                                <Box mt={1.5}>
                                    <Chip
                                        icon={<CheckCircleOutline />}
                                        label="Snowflake Ready"
                                        sx={{
                                            bgcolor: colors.greenAccent[700],
                                            color: colors.grey[100],
                                        }}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="flex-start">
                                <CircularProgress color="inherit" size={24} />
                                <Typography variant="h6" color="textPrimary" ml={2}>
                                    Snowflake Configuration Pending
                                </Typography>
                            </Box>
                        )
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="flex-start">
                            <ErrorOutline sx={{ fontSize: 32, color: colors.redAccent[500] }} />
                            <Typography variant="h6" color="textPrimary" ml={2}>
                                Snowflake Delivery Not Configured
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ borderColor: colors.primary[300] }} />

                <Box>
                    <Typography variant="h5" mb={1.5} color="textPrimary">
                        Preferred Delivery Formats
                    </Typography>

                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Select any combination of available delivery methods for new deliveries.
                        Existing delivery history may still be prioritized automatically during release planning.
                    </Typography>

                    {!availableOptions.length ? (
                        <Box display="flex" alignItems="center">
                            <ErrorOutline sx={{ fontSize: 28, color: colors.redAccent[500] }} />
                            <Typography variant="body1" color="textPrimary" ml={1.5}>
                                No delivery targets are configured yet.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <FormGroup>
                                {availableOptions.includes('csv') ? (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedFormats.includes('csv')}
                                                onChange={() => toggleFormat('csv')}
                                                sx={{
                                                    color: colors.blueAccent[300],
                                                    '&.Mui-checked': {
                                                        color: colors.greenAccent[400],
                                                    },
                                                }}
                                            />
                                        }
                                        label="S3 CSV"
                                    />
                                ) : null}

                                {availableOptions.includes('parquet') ? (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedFormats.includes('parquet')}
                                                onChange={() => toggleFormat('parquet')}
                                                sx={{
                                                    color: colors.blueAccent[300],
                                                    '&.Mui-checked': {
                                                        color: colors.greenAccent[400],
                                                    },
                                                }}
                                            />
                                        }
                                        label="S3 Parquet"
                                    />
                                ) : null}

                                {availableOptions.includes('snowflake') ? (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedFormats.includes('snowflake')}
                                                onChange={() => toggleFormat('snowflake')}
                                                sx={{
                                                    color: colors.blueAccent[300],
                                                    '&.Mui-checked': {
                                                        color: colors.greenAccent[400],
                                                    },
                                                }}
                                            />
                                        }
                                        label="Snowflake Copy"
                                    />
                                ) : null}
                            </FormGroup>

                            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                                {selectedFormats.length ? (
                                    selectedFormats.map((fmt) => (
                                        <Chip
                                            key={fmt}
                                            label={fmt}
                                            sx={{
                                                bgcolor: colors.blueAccent[700],
                                                color: colors.grey[100],
                                            }}
                                        />
                                    ))
                                ) : (
                                    <Chip
                                        label="No preferred formats selected"
                                        sx={{
                                            bgcolor: colors.redAccent[700],
                                            color: colors.grey[100],
                                        }}
                                    />
                                )}
                            </Box>

                            <Box mt={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<SaveOutlined />}
                                    onClick={handleSave}
                                    disabled={!hasChanges || saving}
                                    sx={{
                                        borderColor: colors.blueAccent[200],
                                        color: colors.greenAccent[200],
                                        '&:hover': {
                                            borderColor: colors.primary[100],
                                            backgroundColor: colors.greenAccent[400],
                                        },
                                    }}
                                >
                                    {saving ? 'Saving...' : 'Save Preferred Delivery Formats'}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
                {s3Ready ? (
                    <>
                        <Divider sx={{ borderColor: colors.primary[300] }} />

                        <Box>
                            <Typography variant="h5" mb={1.5} color="textPrimary">
                                Advanced S3 Settings
                            </Typography>

                            <Typography variant="body2" color="textSecondary" mb={2}>
                                Configure custom S3 destination path rules for organizations that require non-standard delivery folders.
                            </Typography>

                            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                <Button
                                    variant="outlined"
                                    startIcon={<TuneOutlined />}
                                    onClick={() => setAdvancedOpen(true)}
                                    sx={{
                                        borderColor: colors.blueAccent[200],
                                        color: colors.greenAccent[200],
                                        '&:hover': {
                                            borderColor: colors.primary[100],
                                            backgroundColor: colors.blueAccent[700],
                                        },
                                    }}
                                >
                                    Advanced S3 Overrides
                                </Button>

                                {Array.isArray(organization?.s3_delivery_overrides) && organization.s3_delivery_overrides.length ? (
                                    <Chip
                                        label={`${organization.s3_delivery_overrides.length} override${organization.s3_delivery_overrides.length === 1 ? '' : 's'} configured`}
                                        sx={{
                                            bgcolor: colors.greenAccent[700],
                                            color: colors.grey[100],
                                        }}
                                    />
                                ) : (
                                    <Chip
                                        label="No overrides configured"
                                        sx={{
                                            bgcolor: colors.primary[300],
                                            color: colors.grey[100],
                                        }}
                                    />
                                )}
                            </Box>

                            {Array.isArray(organization?.s3_delivery_overrides) && organization.s3_delivery_overrides.length ? (
                                <Stack spacing={1} mt={2}>
                                    {organization.s3_delivery_overrides.map((override) => (
                                        <Paper
                                            key={override._id || `${override.product}:${override.file_format}`}
                                            elevation={1}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: '12px',
                                                background: colors.primary[500],
                                            }}
                                        >
                                            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                                <Chip
                                                    label={override.enabled === false ? 'Disabled' : 'Enabled'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: override.enabled === false
                                                            ? colors.redAccent[700]
                                                            : colors.greenAccent[700],
                                                        color: colors.grey[100],
                                                    }}
                                                />

                                                <Chip
                                                    label={override.product}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: colors.blueAccent[700],
                                                        color: colors.grey[100],
                                                    }}
                                                />

                                                <Chip
                                                    label={override.file_format}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: colors.primary[300],
                                                        color: colors.grey[100],
                                                    }}
                                                />

                                                {override.release ? (
                                                    <Chip
                                                        label={override.release}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: colors.primary[300],
                                                            color: colors.grey[100],
                                                        }}
                                                    />
                                                ) : null}
                                            </Box>

                                            <Typography variant="body2" color="textPrimary" mt={1}>
                                                {override.destination_path_template}
                                            </Typography>

                                            {override.label ? (
                                                <Typography variant="caption" color="textSecondary">
                                                    {override.label}
                                                </Typography>
                                            ) : null}
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : null}
                        </Box>

                        <S3DeliveryOverridesAdvanced
                            open={advancedOpen}
                            onClose={() => setAdvancedOpen(false)}
                            organization={organization}
                            organizationId={organizationId}
                            colors={colors}
                        />
                    </>
                ) : null}
            </Stack>
        </Paper>
    );
};

export default DeliveryInfo;